import {
  BankFilled,
  CreditCardFilled,
  DeleteFilled,
  MoreOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Menu } from "antd";
import { useEffect, useState } from "react";
import { PlaidLinkOnSuccessMetadata, usePlaidLink } from "react-plaid-link";
import {
  useDisconnectPlaidLink,
  usePlaidCompleteReconnect,
  useReConnectPlaidLinkSession,
} from "../../api/api";
import { useMessage } from "../../components/notifications/message";
import { useGroupContext } from "../../providers/groups.provider";
import { IAccount } from "../../types/types";
import { capitalize } from "../../utils/functions";

export default function AccountRow({
  account,
  onDeleteClick,
  OnError,
  onSuccess,
}: {
  account?: IAccount;
  OnError: (msg: string) => void;
  onSuccess: (msg: string) => void;
  onDeleteClick: (account?: IAccount) => void;
}) {
  const { messageError, notification } = useMessage();
  const { mutateAsync: reconnectAsync } = useReConnectPlaidLinkSession(
    account?.id,
  );
  const { defaultGroupId } = useGroupContext();

  const { mutateAsync: disconnectAsync } = useDisconnectPlaidLink(account?.id);

  const { mutateAsync: plaidCompleteReconnectAsync } =
    usePlaidCompleteReconnect(account?.id);

  const [linkToken, setLinkToken] = useState<string | null>(null);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onExit: (error) => console.error(error),
    onSuccess: (publicToken: string, meta: PlaidLinkOnSuccessMetadata) =>
      handleReconnectPlaidComplete(publicToken, meta),
  });

  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken, ready]);

  const handleReconnectPlaidComplete = (
    publicToken: string,
    meta: PlaidLinkOnSuccessMetadata,
  ) => {
    plaidCompleteReconnectAsync({
      bankName: meta.institution?.name ?? "unknown",
      token: publicToken,
      bankId: meta.institution?.institution_id,
      groupId: defaultGroupId ?? "",
    })
      .then(() => onSuccess("Account successfully synced"))
      .catch(() =>
        OnError(
          "Unable to connect to bank. Please try again or contact the admin",
        ),
      );
  };

  const handleReConnectOrDisconnect = (account?: IAccount) => {
    if (account) {
      if (!!account.plaidAccount) {
        disconnectAsync({})
          .then(() => {
            onSuccess("Account is disconnected from bank.");
          })
          .catch(() => {
            messageError("Whoops! Please contact help@expensehut.com");
          });
      } else {
        // reconnect
        reconnectAsync({})
          .then((resp) => {
            setLinkToken(resp.link_token);
          })
          .catch((e) => {
            let msg = "Whoops! Please try again.";
            if (e.response.data.statusCode === 403) {
              msg = e.response.data.message;
            }
            OnError(msg);
          });
      }
    } else {
      messageError("Whoops! Please refresh page and try again.");
    }
  };

  return (
    <div
      key={account?.id}
      className="p-4 border border-slate-200 rounded-lg hover:shadow-md hover:border-primary/50 transition cursor-pointer bg-slate-50 hover:bg-slate-100"
    >
      {notification}
      <div className="flex justify-between items-start mb-3">
        <div className="flex flex-row items-center justify-center gap-4">
          <CreditCardFilled />
          <h3 className="font-semibold text-foreground text-lg">
            {capitalize(account?.name)}
          </h3>
        </div>
        <div className="flex gap-2">
          {account?.plaidAccount && (
            <div className="bg-green-300 text-xs flex rounded-full px-3 items-center gap-1 height-[50px]">
              <BankFilled />
              <span>Connected</span>
            </div>
          )}

          {
            <Menu
              className=""
              id="account-options-menu"
              mode="horizontal"
              items={[
                {
                  key: "0",
                  icon: (
                    <div className="">
                      <MoreOutlined />
                    </div>
                  ),
                  children: [
                    {
                      key: "1",
                      label: "Delete",
                      icon: <DeleteFilled className="text-red-500" />,
                      onClick: () => onDeleteClick(account),
                    },
                    {
                      key: "2",
                      disabled: account === undefined,
                      label: account?.plaidAccount
                        ? "Disconnect With Bank"
                        : "Reconnect To Bank",
                      onClick: () => handleReConnectOrDisconnect(account),
                      icon: <SyncOutlined className="text-red-500" />,
                    },
                  ],
                },
              ]}
            />
          }
        </div>
      </div>
    </div>
  );
}
